import type { ModerationAction, PanelModerationAction, ParticipantRole } from '../realtime/frames';

/**
 * Client-side mirror of the server permission matrix (control/internal/httpapi/moderation.go):
 * which moderation actions the participants-panel menu may offer for one row. The server still
 * enforces everything; this only keeps the UI from showing what the local user may not do.
 *
 * - Participants get no actions at all.
 * - Nobody acts on themselves (self-mute is a server no-op, self-kick/role is forbidden).
 * - Moderators act on participants only: never on other moderators or an admin.
 * - Admins act on everyone but themselves and the room owner (the admin row itself).
 * - Stop screen share is offered only while the target is actually sharing.
 */
export function moderationActionsFor(input: {
  readonly ownRole: ParticipantRole;
  readonly targetRole: ParticipantRole;
  readonly isSelf: boolean;
  readonly isSharing: boolean;
}): readonly PanelModerationAction[] {
  if (input.isSelf || input.ownRole === 'participant') {
    return [];
  }
  if (
    input.targetRole === 'admin' ||
    (input.ownRole === 'moderator' && input.targetRole !== 'participant')
  ) {
    return [];
  }
  const actions: PanelModerationAction[] = ['mute'];
  if (input.isSharing) {
    actions.push('stop_share');
  }
  actions.push('kick');
  if (input.ownRole === 'admin') {
    actions.push(input.targetRole === 'participant' ? 'promote' : 'demote');
  }
  return actions;
}

/** True when the header-level Mute all action is offered for the local role. */
export function canMuteAll(ownRole: ParticipantRole): boolean {
  return ownRole === 'admin' || ownRole === 'moderator';
}

/** True when the local role may moderate at all (menu surfaces exist). */
export function canModerate(ownRole: ParticipantRole): boolean {
  return ownRole === 'admin' || ownRole === 'moderator';
}

/** True when the action goes out as a moderate frame (as opposed to a role frame). */
export function isModerateFrame(
  action: PanelModerationAction,
): action is Exclude<ModerationAction, 'end'> {
  return action === 'mute' || action === 'mute_all' || action === 'kick' || action === 'stop_share';
}

/**
 * Stable kinds for the server error codes of rejected moderation/role requests
 * (errors.moderation_* in the i18n catalog); unknown codes map to the generic kind.
 */
export type ModerationErrorKind = 'forbidden' | 'invalid_request' | 'moderation_failed' | 'other';

export function moderationErrorKind(code: string): ModerationErrorKind {
  switch (code) {
    case 'forbidden':
      return 'forbidden';
    case 'invalid_request':
      return 'invalid_request';
    case 'moderation_failed':
      return 'moderation_failed';
    default:
      return 'other';
  }
}
